import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface ShareDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: {
    id: string;
    title: string;
  } | null;
}

export function ShareDocumentDialog({ isOpen, onClose, document }: ShareDocumentDialogProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const handleShare = async () => {
    if (!document || !user) return;

    setIsLoading(true);
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      const senderName = profile?.full_name || 'En Advokai-bruker';

      const { error } = await supabase.functions.invoke('share-document-email', {
        body: {
          documentId: document.id,
          recipientEmail: email,
          senderName,
          documentTitle: document.title
        }
      });

      if (error) throw error;

      toast.success("Dokumentet er sendt via e-post", {
        description: `Hele dokumentet "${document.title}" er nå sendt til ${email}`
      });
      onClose();
      setEmail("");
    } catch (error) {
      console.error('Error sharing document:', error);
      toast.error("Kunne ikke dele dokumentet. Vennligst prøv igjen.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Del dokument via e-post</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="email">E-postadresse</Label>
            <Input
              id="email"
              type="email"
              placeholder="ola.nordmann@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleShare}
            disabled={!email || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sender...
              </>
            ) : (
              'Del dokument'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}